import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { COMMANDS, CREATED_ITEM, OPERATION, itemIdOf, revisionIn } from "./catalogOperations"

const STATUSES = ["pending", "completed", "failed"]
const REFUSED_AT_ONCE = [400, 401, 403, 404, 422]
const errorCode = error => {
  const status = error?.response?.status
  if ([401, 403].includes(status)) return "SUPER_ADMIN_REQUIRED"
  const message = error?.response?.data?.message
  return typeof message === "string" && /^[A-Z_]+$/.test(message) ? message : "INVALID_FEEDBACK_CATALOG_OPERATION"
}

// The catalog commands, confirmed as System Settings' maintenance operation (useMaintenanceOperation): each command gets its
// own `operationId`, the gateway answers 202, and the receipt (GET …/catalog-operations/:operationId) is read after the
// `accounts-response` socket signal or by polling. `start(commands)` sends a Save's commands one after another, each with the
// revision from the previous receipt (one change per command). `onSettled({ status, code, details, receipts, saved })` runs
// once at the end: `saved` says whether a command of the Save changed the catalog. A lost answer leaves `unknown` with
// `retry()`, which sends the same request again.
export default function useCatalogOperation({ actorId, onSettled }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const [state, setState] = useState({ status: "idle" })
  const active = useRef(null), settled = useRef(onSettled)
  settled.current = onSettled
  useEffect(() => () => { active.current?.cancel(); active.current = null }, [])

  const start = useCallback(commands => {
    if (!actorId || !commands?.length || (active.current && !active.current.ended)) return false
    const run = { ended: false, receipts: [] }
    active.current = run
    const finish = result => {
      run.ended = true
      run.step?.stop()
      const outcome = { ...result, receipts: run.receipts, saved: run.receipts.length > 0 }
      setState(outcome)
      settled.current?.(outcome)
    }
    const step = index => {
      const payload = { ...commands[index] }
      const previous = run.receipts[index - 1]
      if (payload.itemId === CREATED_ITEM) {
        // The item the Save's first command created: its code was given by Accounts at save.
        const created = previous?.items?.[0]
        if (!created) { finish({ status: "failed", code: "INVALID_FEEDBACK_CATALOG_OPERATION" }); return }
        payload.itemId = itemIdOf(created)
        payload.revision = created.revision
      } else if (previous && payload.itemId && Number.isSafeInteger(payload.revision)) payload.revision = revisionIn(previous, payload.itemId) ?? payload.revision
      const operation = { operationId: newPlayerOperationId(), polls: 0, readFailures: 0, attempts: 0 }
      run.step = operation
      const current = () => active.current === run && !run.ended && run.step === operation
      const schedule = delay => { clearTimeout(operation.timer); operation.timer = setTimeout(poll, delay) }
      const onSocket = message => {
        if (message?.operation !== OPERATION || message.operationId !== operation.operationId) return
        if (message.success === false) operation.socketRefusal = message.message
        void poll()
      }
      operation.stop = () => { clearTimeout(operation.timer); socket?.off("accounts-response", onSocket) }
      const poll = async () => {
        if (!current() || operation.reading) return
        operation.reading = true
        operation.polls += 1
        try {
          const receipt = (await get(`${COMMANDS}/${operation.operationId}`, { timeout: 5000 }))?.data
          if (!current()) return
          if (receipt?.operationId !== operation.operationId || receipt.actorId !== actorId || !STATUSES.includes(receipt.status)) throw new Error("Invalid receipt")
          operation.readFailures = 0
          if (receipt.status === "completed") {
            operation.stop()
            run.receipts.push(receipt)
            if (index + 1 < commands.length) step(index + 1)
            else finish({ status: "completed" })
          } else if (receipt.status === "failed") finish({ status: "failed", code: receipt.message, details: receipt.details })
          else if (operation.polls >= 20) setState({ status: "unknown", code: "AWAITING" })
        } catch (error) {
          if (!current()) return
          const status = error?.response?.status
          if ([401, 403].includes(status)) finish({ status: "failed", code: "SUPER_ADMIN_REQUIRED" })
          else if (status === 404 && operation.socketRefusal) finish({ status: "failed", code: operation.socketRefusal })
          else if (status === 404) { if (operation.polls >= 20) setState({ status: "unknown", code: "AWAITING" }) } // not delivered yet
          else if (++operation.readFailures >= 3) setState({ status: "unknown", code: "UNCONFIRMED" })
        } finally {
          operation.reading = false
          if (current()) schedule(operation.polls < 20 ? 1500 : 10000)
        }
      }
      operation.send = async () => {
        if (!current() || operation.sending) return
        operation.sending = true
        const first = ++operation.attempts === 1
        try {
          await post(COMMANDS, { ...payload, operationId: operation.operationId, ...(socketClientId && { socketClientId }) },
            { timeout: 10000, headers: { "X-Correlation-Id": newPlayerOperationId() } })
        } catch (error) {
          if (!current()) return
          if (first && REFUSED_AT_ONCE.includes(error?.response?.status)) finish({ status: "failed", code: errorCode(error) })
          else setState({ status: "unknown", code: "UNCONFIRMED" })
        } finally { operation.sending = false }
      }
      socket?.on("accounts-response", onSocket)
      schedule(500)
      void operation.send()
    }
    run.cancel = () => { run.ended = true; run.step?.stop() }
    setState({ status: "pending" })
    step(0)
    return true
  }, [actorId, socket, socketClientId])

  const retry = useCallback(() => {
    const run = active.current
    if (!run || run.ended) return
    setState({ status: "pending" })
    void run.step?.send()
  }, [])
  // A new dialog starts without the previous result, never discarding a change still waiting for its receipt.
  const reset = useCallback(() => { if (!active.current || active.current.ended) setState({ status: "idle" }) }, [])
  return { ...state, busy: ["pending", "unknown"].includes(state.status), start, retry, reset }
}
